// The write path, and the one rule the owner stated as non-negotiable:
// OPTIMISTIC UI IS NEVER KEPT AFTER A SERVER REFUSAL.
//
// TanStack Query makes it easy to violate that by accident. `onMutate` snapshots and patches the
// cache; if `onError` forgets to roll back — or rolls back to a snapshot taken from a DIFFERENT query
// key — the refused change stays on screen and the planner believes it happened. So the rollback is
// not a callback here, it is the shape of the helper: `optimistic()` cannot be called without both.
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConflictError, UnauthorizedError } from '../api/client.ts';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A PLANNING VERDICT IS NEVER SERVED FROM CACHE WITHOUT ASKING. Conflicts, capacity and the
      // schedule are the server's opinion about facts that other people are changing right now;
      // showing yesterday's answer as though it were current is the failure this whole app exists to
      // prevent. Lists that only name things (people, teams) may be cached.
      staleTime: 0,
      refetchOnWindowFocus: true,
      retry: (count, err) => {
        // Never retry a refusal. A 401 is not transient, a 409 means the world moved and the request
        // must be rebuilt from fresh state, and a 403 will not become true by asking again.
        if (err instanceof UnauthorizedError || err instanceof ConflictError) return false;
        return count < 2;
      },
    },
    mutations: { retry: false },
  },
});

export const keys = {
  me: ['me'] as const,
  projects: ['projects'] as const,
  people: ['people'] as const,
  teams: ['teams'] as const,
  crew: ['crew'] as const,
  schedule: (from: string, to: string, actorId?: string) => ['schedule', from, to, actorId ?? null] as const,
  mySchedule: (from: string, to: string) => ['my-schedule', from, to] as const,
  phases: (p: number) => ['phases', p] as const,
  workpackages: (p: number) => ['workpackages', p] as const,
  conflicts: (p: number) => ['conflicts', p] as const,
  capacity: (p: number) => ['capacity', p] as const,
  assignments: (p: number) => ['assignments', p] as const,
  projectResources: (p: number) => ['project-resources', p] as const,
};

/** A fresh idempotency key per USER INTENT. Not per retry — a retry must reuse the same one. */
export const correlationId = (): string =>
  (globalThis.crypto?.randomUUID?.() ?? `c-${Date.now()}-${Math.random().toString(36).slice(2)}`);

/**
 * Everything a planning write must invalidate.
 *
 * DELIBERATELY BROADER THAN THE THING THAT CHANGED. Moving one workpackage can change the project's
 * conflict verdict, its capacity numbers, and the schedule of every resource booked on it. Invalidating
 * only the row that moved is how a UI ends up showing a stale overload badge next to a moved task.
 */
export function invalidatePlanning(qc: QueryClient, projectId?: number) {
  qc.invalidateQueries({ queryKey: ['schedule'] });
  qc.invalidateQueries({ queryKey: ['my-schedule'] });
  if (projectId !== undefined) {
    qc.invalidateQueries({ queryKey: keys.conflicts(projectId) });
    qc.invalidateQueries({ queryKey: keys.capacity(projectId) });
    qc.invalidateQueries({ queryKey: keys.workpackages(projectId) });
    qc.invalidateQueries({ queryKey: keys.assignments(projectId) });
  }
}

export interface OptimisticSpec<TVars, TData> {
  mutationFn: (vars: TVars) => Promise<TData>;
  /** Patch the cache. MUST return the exact snapshot needed to undo itself. */
  apply: (qc: QueryClient, vars: TVars) => unknown;
  /** Undo `apply`, given whatever it returned. Not optional — see the header. */
  revert: (qc: QueryClient, snapshot: unknown) => void;
  projectId?: (vars: TVars) => number;
  onRefused?: (err: Error, vars: TVars) => void;
}

/**
 * An optimistic mutation that cannot forget to roll back.
 *
 * The refusal path always reverts, then always refetches. Reverting alone is not enough: the snapshot
 * describes what this client believed BEFORE its own change, which may already be wrong if the reason
 * for the refusal was somebody else's write. Only the server knows what is true now.
 */
export function useOptimistic<TVars, TData>(spec: OptimisticSpec<TVars, TData>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: spec.mutationFn,
    onMutate: async (vars: TVars) => {
      await qc.cancelQueries({ queryKey: ['schedule'] });
      return { snapshot: spec.apply(qc, vars) };
    },
    onError: (err, vars, ctx) => {
      if (ctx) spec.revert(qc, ctx.snapshot);
      invalidatePlanning(qc, spec.projectId?.(vars));
      spec.onRefused?.(err as Error, vars);
    },
    onSuccess: (_d, vars) => invalidatePlanning(qc, spec.projectId?.(vars)),
  });
}
